import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { endpointLink } from '../../index';
import { useApiAccessToken } from '../../hooks/useApiAccessToken';

type RawSensorEditRow = {
  deviceEUI: string;
  deviceID: string | null;
  Type: string | null;
  Class: string | null;
  Group: string | null;
  Location: string | null;
};

type SensorEditRow = {
  deviceEUI: string;
  deviceID: string;
  Type: string;
  Class: string;
  Group: string;
  Location: string;
};

type ThresholdRow = {
  localId: string;
  originalClass: string;
  Class: string;
  warningF: string;
  alarmF: string;
  isNew: boolean;
};

type ClassThresholdApiRow = {
  Class: string;
  warningF: number;
  alarmF: number;
};

const decodeMaybeJsonString = (raw: string | null | undefined): string => {
  if (raw == null) return '';
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') return parsed;
    if (Array.isArray(parsed)) return parsed.join(', ');
    return String(parsed ?? '');
  } catch {
    return raw;
  }
};

const normalizeSensorRow = (row: RawSensorEditRow): SensorEditRow => ({
  deviceEUI: row.deviceEUI ?? '',
  deviceID: row.deviceID ?? '',
  Type: (row.Type ?? '').trim(),
  Class: decodeMaybeJsonString(row.Class),
  Group: decodeMaybeJsonString(row.Group),
  Location: row.Location ?? '',
});

const makeThresholdId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const SensorsEditGrid: React.FC = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const getApiAccessToken = useApiAccessToken({ returnTo: `${location.pathname}${location.search}` });
  const [searchParams, setSearchParams] = useSearchParams();

  const initialGroup = (searchParams.get('group') || 'all').trim() || 'all';
  const [groupFilter, setGroupFilter] = useState(initialGroup);

  const [thresholdRows, setThresholdRows] = useState<ThresholdRow[]>([]);
  const [deletedThresholdClasses, setDeletedThresholdClasses] = useState<string[]>([]);
  const [sensorEdits, setSensorEdits] = useState<Record<string, { Location?: string; Class?: string }>>({});
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchAuthed = async <T,>(url: string): Promise<T> => {
    const token = await getApiAccessToken();
    const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Request failed (${response.status})`);
    }
    return response.json() as Promise<T>;
  };

  const classThresholdsQuery = useQuery<ClassThresholdApiRow[]>({
    queryKey: ['class-thresholds'],
    queryFn: () => fetchAuthed<ClassThresholdApiRow[]>(`${endpointLink}class-thresholds`),
    staleTime: 60_000,
  });

  const sensorsQuery = useQuery<SensorEditRow[]>({
    queryKey: ['sensors-edit', groupFilter],
    queryFn: async () => {
      const rows = await fetchAuthed<RawSensorEditRow[]>(
        `${endpointLink}sensors/edit?group=${encodeURIComponent(groupFilter)}`,
      );
      return rows.map(normalizeSensorRow);
    },
    staleTime: 30_000,
  });

  const allSensorsQuery = useQuery<SensorEditRow[]>({
    queryKey: ['sensors-edit', 'all-options'],
    queryFn: async () => {
      const rows = await fetchAuthed<RawSensorEditRow[]>(`${endpointLink}sensors/edit?group=all`);
      return rows.map(normalizeSensorRow);
    },
    staleTime: 30_000,
  });

  useEffect(() => {
    const rows = classThresholdsQuery.data ?? [];
    setThresholdRows(
      rows.map((r) => ({
        localId: makeThresholdId(),
        originalClass: r.Class,
        Class: r.Class,
        warningF: String(r.warningF),
        alarmF: String(r.alarmF),
        isNew: false,
      })),
    );
    setDeletedThresholdClasses([]);
  }, [classThresholdsQuery.data]);

  useEffect(() => {
    setSensorEdits({});
  }, [groupFilter, sensorsQuery.data]);

  const groupOptions = useMemo(() => {
    const groups = new Set<string>();
    (allSensorsQuery.data ?? []).forEach((s) => {
      s.Group
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((g) => groups.add(g));
    });
    return ['all', ...Array.from(groups).sort((a, b) => a.localeCompare(b))];
  }, [allSensorsQuery.data]);

  // Class names are matched case-insensitively against Class Thresholds
  // (e.g. "Freezer" on a sensor vs "freezer" in the thresholds table refer to
  // the same class). Canonicalize every class to lowercase so spellings like
  // "Freezer"/"freezer" or "Kiln"/"kiln" collapse into one consistent
  // dropdown entry instead of showing up as separate options.
  const classOptions = useMemo(() => {
    const keys = new Set<string>();
    (allSensorsQuery.data ?? []).forEach((s) => {
      const trimmed = s.Class.trim();
      if (trimmed) keys.add(trimmed.toLowerCase());
    });
    thresholdRows.forEach((r) => {
      const trimmed = r.Class.trim();
      if (trimmed) keys.add(trimmed.toLowerCase());
    });
    return Array.from(keys).sort((a, b) => a.localeCompare(b));
  }, [allSensorsQuery.data, thresholdRows]);

  // Resolve a sensor's raw Class value to the lowercase form shown in the
  // dropdown, so e.g. a sensor stored as "Freezer" renders as the selected
  // "freezer" option instead of appearing blank/unselected.
  const resolveClassOption = (rawClass: string): string => rawClass.trim().toLowerCase();

  const onGroupChange = (event: SelectChangeEvent<string>) => {
    const next = event.target.value;
    setGroupFilter(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('group', next);
      return p;
    });
  };

  const onThresholdField = (localId: string, key: 'Class' | 'warningF' | 'alarmF', value: string) => {
    setThresholdRows((prev) =>
      prev.map((row) => (row.localId === localId ? { ...row, [key]: value } : row)),
    );
  };

  const onAddThresholdRow = () => {
    setThresholdRows((prev) => [
      ...prev,
      {
        localId: makeThresholdId(),
        originalClass: '',
        Class: '',
        warningF: '',
        alarmF: '',
        isNew: true,
      },
    ]);
  };

  const onDeleteThresholdRow = (row: ThresholdRow) => {
    setThresholdRows((prev) => prev.filter((r) => r.localId !== row.localId));
    if (!row.isNew && row.originalClass.trim()) {
      setDeletedThresholdClasses((prev) => Array.from(new Set([...prev, row.originalClass.trim()])));
    }
  };

  const thresholdMutation = useMutation({
    mutationFn: async () => {
      const updates = thresholdRows
        .map((r) => ({
          Class: r.Class.trim(),
          originalClass: r.originalClass.trim() || r.Class.trim(),
          warningF: Number(r.warningF),
          alarmF: Number(r.alarmF),
        }))
        .filter((r) => r.Class.length > 0 && Number.isFinite(r.warningF) && Number.isFinite(r.alarmF));

      const token = await getApiAccessToken();
      const response = await fetch(`${endpointLink}class-thresholds`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates,
          deletes: deletedThresholdClasses,
        }),
      });
      const text = await response.text();
      if (!response.ok) throw new Error(text || `Failed to save class thresholds (${response.status})`);
      return text ? JSON.parse(text) : {};
    },
    onSuccess: async () => {
      setSuccessMessage('Class thresholds saved.');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['class-thresholds'] }),
        queryClient.invalidateQueries({ queryKey: ['sensors-edit'] }),
        queryClient.invalidateQueries({ queryKey: ['sensorMetadata'] }),
      ]);
    },
    onError: (err) => {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to save class thresholds.');
    },
  });

  const sensorMutation = useMutation({
    mutationFn: async () => {
      const baseline = new Map((sensorsQuery.data ?? []).map((row) => [row.deviceEUI, row]));
      const updates = Object.entries(sensorEdits)
        .map(([deviceEUI, patch]) => {
          const original = baseline.get(deviceEUI);
          if (!original) return null;
          const nextClass = patch.Class;
          const nextLocation = patch.Location;
          const out: Record<string, string> = { deviceEUI };
          let touched = false;
          if (typeof nextClass === 'string' && nextClass !== original.Class) {
            out.Class = nextClass;
            touched = true;
          }
          if (typeof nextLocation === 'string' && nextLocation !== original.Location) {
            out.Location = nextLocation;
            touched = true;
          }
          return touched ? out : null;
        })
        .filter((u): u is Record<string, string> => u !== null);

      if (updates.length === 0) {
        throw new Error('No sensor changes to save.');
      }

      const token = await getApiAccessToken();
      const response = await fetch(`${endpointLink}sensors/edit`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });
      const text = await response.text();
      if (!response.ok) throw new Error(text || `Failed to save sensors (${response.status})`);
      return text ? JSON.parse(text) : {};
    },
    onSuccess: async () => {
      setSuccessMessage('Sensor assignments saved.');
      setSensorEdits({});
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sensors-edit'] }),
        queryClient.invalidateQueries({ queryKey: ['sensorMetadata'] }),
      ]);
    },
    onError: (err) => {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to save sensors.');
    },
  });

  const isLoading = classThresholdsQuery.isLoading || sensorsQuery.isLoading || allSensorsQuery.isLoading;

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
              Edit Sensors
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update Location labels, assign Classes to sensors, and define warning/alarm thresholds per Class.
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="sensor-edit-group">Group</InputLabel>
            <Select
              labelId="sensor-edit-group"
              value={groupFilter}
              label="Group"
              onChange={onGroupChange}
            >
              {groupOptions.map((g) => (
                <MenuItem key={g} value={g}>
                  {g === 'all' ? 'All' : g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {isLoading ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Class Thresholds (°F)
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Class</TableCell>
                    <TableCell>Warning °F</TableCell>
                    <TableCell>Alarm °F</TableCell>
                    <TableCell width={70} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {thresholdRows.map((row) => (
                    <TableRow key={row.localId}>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.Class}
                          onChange={(e) => onThresholdField(row.localId, 'Class', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          value={row.warningF}
                          onChange={(e) => onThresholdField(row.localId, 'warningF', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          value={row.alarmF}
                          onChange={(e) => onThresholdField(row.localId, 'alarmF', e.target.value)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button color="error" onClick={() => onDeleteThresholdRow(row)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddThresholdRow}>
                Add Class
              </Button>
              <Button
                variant="contained"
                onClick={() => thresholdMutation.mutate()}
                disabled={thresholdMutation.isPending}
              >
                {thresholdMutation.isPending ? 'Saving…' : 'Save Class Thresholds'}
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 1 }}>
              Sensors ({groupFilter === 'all' ? 'All groups' : groupFilter})
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>deviceID</TableCell>
                    <TableCell>deviceEUI</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Group</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Class</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(sensorsQuery.data ?? []).map((row) => {
                    const patch = sensorEdits[row.deviceEUI] || {};
                    const locationValue = patch.Location ?? row.Location;
                    const classValue = resolveClassOption(patch.Class ?? row.Class);
                    return (
                      <TableRow key={row.deviceEUI}>
                        <TableCell>{row.deviceID || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.deviceEUI}</TableCell>
                        <TableCell>{row.Type || '—'}</TableCell>
                        <TableCell>{row.Group || '—'}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={locationValue}
                            onChange={(e) =>
                              setSensorEdits((prev) => ({
                                ...prev,
                                [row.deviceEUI]: {
                                  ...prev[row.deviceEUI],
                                  Location: e.target.value,
                                },
                              }))
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 220 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={classValue}
                              onChange={(e) =>
                                setSensorEdits((prev) => ({
                                  ...prev,
                                  [row.deviceEUI]: {
                                    ...prev[row.deviceEUI],
                                    Class: e.target.value,
                                  },
                                }))
                              }
                            >
                              {classOptions.map((cls) => (
                                <MenuItem key={cls} value={cls}>
                                  {cls}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => sensorMutation.mutate()}
                disabled={sensorMutation.isPending}
              >
                {sensorMutation.isPending ? 'Saving…' : 'Save Sensors'}
              </Button>
            </Stack>
          </>
        )}
      </Paper>

      <Snackbar open={Boolean(errorMessage)} autoHideDuration={6000} onClose={() => setErrorMessage('')}>
        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={Boolean(successMessage)} autoHideDuration={3500} onClose={() => setSuccessMessage('')}>
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SensorsEditGrid;
